// Missed-dose backfill dashboard widget (MISSED_DOSE_BACKFILL_PLAN.md Task 3).
// Self-fetching + auto-hiding, following the PtReminderWidget pattern: renders
// null once the sweep finds nothing.

import { useEffect, useState } from "react";
import type { BackfillSlot } from "@medassist/shared";
import { resolveCatchUpWidgets, resolveProactiveSettings } from "@medassist/shared";
import { getBackfillSlots, ApiError } from "../../../lib/api.js";
import { AlertTriangleIcon } from "../../icons.js";
import { MissedDoseBackfill } from "../../MissedDoseBackfill.js";
import type { WidgetProps } from "../types.js";

export function MissedDosesWidget({ patient, onError, onReload }: WidgetProps) {
  const [slots, setSlots] = useState<BackfillSlot[] | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  // D-CU-5 (CATCHUP_WIDGETS_PLAN.md): the medication catch-up toggle in Preferences
  // gates this widget, same as the other five per-type catch-up toggles.
  const enabled = resolveCatchUpWidgets(
    resolveProactiveSettings(patient.proactiveSuggestions).catchUpWidgets
  ).medication;

  function refetch() {
    if (!enabled) return;
    getBackfillSlots(patient.patientId)
      .then((res) => setSlots(res.slots))
      .catch((err) => onError(err instanceof ApiError ? err.message : "Couldn't load missed doses"));
  }

  useEffect(refetch, [patient.patientId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  if (slots === null) {
    return (
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="mb-2 flex items-center gap-2">
          <AlertTriangleIcon size={16} className="text-amber-500 dark:text-amber-400" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Missed doses</h2>
        </div>
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
      </section>
    );
  }

  if (slots.length === 0) return null;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangleIcon size={16} className="text-amber-500 dark:text-amber-400" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Missed doses</h2>
        <button
          onClick={() => setReviewOpen(true)}
          className="ml-auto rounded-full bg-amber-100 dark:bg-amber-900/40 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/40"
        >
          Review
        </button>
      </div>
      <p className="text-sm text-amber-700 dark:text-amber-300">
        {slots.length} scheduled dose{slots.length !== 1 ? "s" : ""} from the past week weren&apos;t logged
      </p>
      <ul className="mt-1 space-y-0.5">
        {slots.slice(0, 3).map((s) => (
          <li key={s.slotId} className="truncate text-xs text-gray-500 dark:text-gray-400">
            {s.name} — {s.dayKey}
          </li>
        ))}
      </ul>
      {slots.length > 3 && (
        <p className="text-xs text-gray-400 dark:text-gray-500">+{slots.length - 3} more</p>
      )}
      {confirmation && <p className="mt-2 text-xs text-green-700 dark:text-green-300">{confirmation}</p>}

      {reviewOpen && (
        <MissedDoseBackfill
          patientId={patient.patientId}
          slots={slots}
          onClose={() => {
            setReviewOpen(false);
            refetch();
          }}
          onLogged={(count) => {
            setConfirmation(`Logged ${count} dose${count !== 1 ? "s" : ""} ✓`);
            setTimeout(() => setConfirmation(null), 5000);
            // A backfilled dose satisfies alreadyHandled() server-side, so the
            // proactive medication suggestion must be recomputed — otherwise the
            // dashboard keeps proposing a dose the user just marked taken.
            onReload();
          }}
        />
      )}
    </section>
  );
}
